import { BRAND, formatUsd } from '@console/shared'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, BookOpen, FlaskConical, KeyRound, Wallet } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Mosaic } from '../components/brand/Mosaic'
import { DocsLink } from '../components/DocsLink'
import { CopyButton, Stat, StatRow } from '../components/ui/misc'
import { api, useActiveOrg, useConfig, useMe, useModels } from '../lib/api'

export function HomePage() {
  const { data: me } = useMe()
  const { org } = useActiveOrg()
  const { data: cfg } = useConfig()
  const { data: home } = useQuery({
    queryKey: ['home'],
    queryFn: () => api<{ balanceNano: number; activeKeys: number; totalCalls: number; defaultModel: string | null }>('/api/home'),
  })
  const { data: models } = useModels()
  const def = models?.data.find((m) => m.isDefault) ?? models?.data[0]
  const base = cfg?.publicApiUrl ?? window.location.origin
  const first = me?.user?.name?.split(' ')[0]
  const curl = `curl ${base}/v1/decide \\
  -H "Authorization: Bearer $TRODO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "${def?.id ?? BRAND.defaultModelId}",
    "state": "User: I was charged twice. Agent: I have refunded the duplicate charge.",
    "questions": {
      "resolved": {"type": "noul", "instructions": "Did the agent resolve the issue?"}
    }
  }'`

  const steps = [
    { done: (home?.activeKeys ?? 0) > 0, to: '/api-keys', icon: KeyRound, title: 'Create an API key' },
    { done: (home?.totalCalls ?? 0) > 0, to: '/playground', icon: FlaskConical, title: 'Try a request in the playground' },
    { done: false, to: '/docs', icon: BookOpen, title: 'Read the API guide' },
    { done: false, to: '/settings/billing', icon: Wallet, title: 'Add credits' },
  ]

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl px-5 py-8 md:px-8">
        <p className="eyebrow">{org?.name}</p>
        <h1 className="headline mt-3 text-[34px] leading-[1.08]">
          <span className="pre">{first ? `Welcome, ${first}.` : 'Welcome.'}</span>
          Every decision, one forward pass.
        </h1>
        <p className="mt-3 max-w-xl text-[14.5px] leading-relaxed text-ink-secondary">
          {BRAND.modelFamily} reads a trace, a conversation or a tool catalogue and returns a calibrated probability for every option you
          ask about, or the exact value it states.
        </p>

        <div className="mt-6">
          <StatRow>
          <Stat label="Balance" value={home ? formatUsd(home.balanceNano) : '—'} hint={<Link to="/settings/billing" className="text-brand">Add credits</Link>} />
          <Stat label="Active API keys" value={home?.activeKeys ?? '—'} />
          <Stat label="Requests so far" value={home?.totalCalls?.toLocaleString() ?? '—'} />
          </StatRow>
        </div>

        <h2 className="mt-10 text-[15px] font-medium tracking-[-0.01em] text-ink">Get started</h2>
        <div className="mt-3 divide-y divide-[color:var(--border-subtle)] border-y border-line-subtle">
          {steps.map((s) => {
            const cls = 'group flex items-center gap-3 px-1 py-3 transition-colors hover:bg-surface-hover'
            const body = (
              <>
                <s.icon strokeWidth={1.6} className={`h-4 w-4 shrink-0 ${s.done ? 'text-success' : 'text-ink-muted'}`} />
                <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] font-medium text-ink">
                  {s.title}
                  {s.done && <span className="text-[11px] font-normal text-success">done</span>}
                </div>
                <ArrowRight className="h-4 w-4 shrink-0 text-ink-disabled transition-transform group-hover:translate-x-0.5" />
              </>
            )
            // the API guide may live on the docs site (a new tab)
            return s.to === '/docs' ? (
              <DocsLink key={s.to} className={cls}>
                {body}
              </DocsLink>
            ) : (
              <Link key={s.to} to={s.to} className={cls}>
                {body}
              </Link>
            )
          })}
        </div>

        <h2 className="mt-10 text-[15px] font-medium tracking-[-0.01em] text-ink">Your first call</h2>
        <div className="relative mt-3 overflow-hidden rounded-2xl border border-line">
          <Mosaic className="absolute inset-0 h-full w-full" palette="ember" seed={20} />
          <div className="relative px-5 py-8 sm:px-10 sm:py-10">
            <div className="overflow-hidden rounded-xl border border-[#2a2a2d] bg-[#141415] text-[#f1f3f6] shadow-[0_30px_60px_-20px_rgba(60,40,20,0.45)]">
              <div className="flex items-center justify-between border-b border-[#2a2a2d] px-4 py-2.5">
                <span className="flex items-center gap-2 text-[12.5px] font-medium">
                  <span className="h-2 w-2 rounded-full bg-[#3fb96b]" /> POST /v1/decide
                </span>
                <CopyButton value={curl} className="!text-[#c9c5bd] hover:!bg-white/10 hover:!text-white" />
              </div>
              <pre className="overflow-x-auto p-4 text-[12px] leading-relaxed text-[#e6e2da]">{curl}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
